import { useState, useCallback, type FormEvent } from 'react';
import { useClerk, useSignIn } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { GoogleIcon } from './google-icon.js';
import { cn } from '../../lib/cn.js';

type Step = 'credentials' | 'reset-request' | 'reset-code';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TFn = (key: string, defaultValue?: any) => string;

function errorMessage(clerkError: unknown, fallback: string, t: TFn): string {
  // ClerkError shape: { errors: [{ code, message, longMessage }], raw }
  if (
    clerkError &&
    typeof clerkError === 'object' &&
    'errors' in clerkError &&
    Array.isArray((clerkError as { errors: unknown[] }).errors)
  ) {
    const first = (clerkError as { errors: Array<{ longMessage?: string; message?: string }> })
      .errors[0];
    if (first?.longMessage) return first.longMessage;
    if (first?.message) return first.message;
  }
  return t('auth.signin.errors.generic', fallback);
}

export function SignInForm() {
  const { t } = useTranslation('common');
  const { signIn, fetchStatus } = useSignIn();
  const clerk = useClerk();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [googleInFlight, setGoogleInFlight] = useState(false);

  const loading = fetchStatus === 'fetching';

  const handleSignIn = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');

      if (!signIn) return;
      if (!email || !password) {
        setError(t('auth.signin.errors.missingFields', 'Please enter both email and password.'));
        return;
      }

      const { error: createError } = await signIn.create({ identifier: email });
      if (createError) {
        setError(errorMessage(createError, 'Unable to start sign in.', t));
        return;
      }

      const { error: pwError } = await signIn.password({ password });
      if (pwError) {
        setError(errorMessage(pwError, 'Incorrect password.', t));
        return;
      }

      if (signIn.status === 'complete') {
        const { error: finalizeError } = await signIn.finalize();
        if (finalizeError) {
          setError(errorMessage(finalizeError, 'Unable to finalize sign in.', t));
          return;
        }
        navigate('/dashboard');
        return;
      }

      // Unexpected status (MFA required, etc.) — surface a helpful message
      setError(
        t(
          'auth.signin.errors.unexpectedStatus',
          'Additional verification is required. Please try again.',
        ),
      );
    },
    [signIn, email, password, t, navigate],
  );

  const handleGoogle = useCallback(async () => {
    if (googleInFlight) return;
    setError('');
    // Use the stable signIn resource off the Clerk client — useSignIn()'s
    // returned object is the "future" API (SignInFutureResource) which
    // only exposes `.sso(...)` and was silently no-op'ing for this app.
    // client.signIn is the documented v5 SignInResource with the canonical
    // .authenticateWithRedirect() method.
    const stableSignIn = clerk.client?.signIn;
    if (!stableSignIn) {
      console.warn('[auth] Google sign-in clicked before Clerk SDK ready');
      setError(
        t(
          'auth.signin.errors.notReady',
          'Sign-in is still initializing. Please try again in a moment.',
        ),
      );
      return;
    }
    setGoogleInFlight(true);
    // Safety watchdog: if 6s elapse without the browser navigating away,
    // re-enable the button + show an error. Should be unreachable in the
    // happy path (authenticateWithRedirect navigates synchronously) — kept
    // as belt-and-suspenders against any future SDK regression.
    const watchdog = window.setTimeout(() => {
      setGoogleInFlight(false);
      setError(
        t('auth.signin.errors.stalled', "Google didn't open. Wait a moment, then try again."),
      );
    }, 6000);
    try {
      // authenticateWithRedirect navigates synchronously to Clerk's FAPI,
      // which in turn redirects to Google. The default `continueSignIn:
      // false` creates a fresh signIn resource server-side every time, so
      // stale state from a previously cancelled attempt is replaced — no
      // separate reset needed.
      await stableSignIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: `${window.location.origin}/dashboard`,
      });
    } catch (err) {
      console.error('[auth] signIn.authenticateWithRedirect threw', err);
      const code = (err as { errors?: Array<{ code?: string }> }).errors?.[0]?.code;
      const isRateLimited =
        code === 'rate_limit_exceeded' ||
        code === 'too_many_requests' ||
        (err instanceof Error && /429|rate.?limit/i.test(err.message));
      setError(
        isRateLimited
          ? t(
              'auth.signin.errors.rateLimited',
              'Too many sign-in attempts. Wait a minute and try again.',
            )
          : errorMessage(err, 'Google sign in failed. Check your connection and try again.', t),
      );
    } finally {
      window.clearTimeout(watchdog);
      setGoogleInFlight(false);
    }
  }, [clerk, t, googleInFlight]);

  const handleRequestReset = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');
      if (!signIn) return;
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError(t('auth.signin.errors.invalidEmail', 'Please enter a valid email address.'));
        return;
      }
      const { error: createError } = await signIn.create({ identifier: email });
      if (createError) {
        setError(errorMessage(createError, 'Unable to start password reset.', t));
        return;
      }
      const { error: sendError } = await signIn.resetPasswordEmailCode.sendCode();
      if (sendError) {
        setError(errorMessage(sendError, 'Unable to send reset code.', t));
        return;
      }
      setStep('reset-code');
    },
    [signIn, email, t],
  );

  const handleSubmitReset = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');
      if (!signIn) return;
      if (!code) {
        setError(t('auth.signin.errors.missingCode', 'Please enter the code from your email.'));
        return;
      }
      if (!newPassword || newPassword.length < 8) {
        setError(
          t('auth.signin.errors.weakPassword', 'New password must be at least 8 characters.'),
        );
        return;
      }
      const { error: verifyError } = await signIn.resetPasswordEmailCode.verifyCode({ code });
      if (verifyError) {
        setError(errorMessage(verifyError, 'That code was not recognised.', t));
        return;
      }
      const { error: submitError } = await signIn.resetPasswordEmailCode.submitPassword({
        password: newPassword,
      });
      if (submitError) {
        setError(errorMessage(submitError, 'Unable to set new password.', t));
        return;
      }
      if (signIn.status === 'complete') {
        // Destructure to surface any finalize error instead of navigating to
        // /dashboard in an unauthenticated state (which ProtectedRoute would
        // bounce back to /sign-in, producing a visible flash).
        const { error: finalizeError } = await signIn.finalize();
        if (finalizeError) {
          setError(errorMessage(finalizeError, 'Unable to finalize password reset.', t));
          return;
        }
        navigate('/dashboard');
      }
    },
    [signIn, code, newPassword, t, navigate],
  );

  if (step === 'reset-request') {
    return (
      <form
        onSubmit={handleRequestReset}
        className="flex flex-col gap-4"
        data-testid="signin-reset-request"
      >
        <FieldLabel htmlFor="reset-email">
          {t('auth.signin.emailLabel', 'Email address')}
        </FieldLabel>
        <GlassInput
          id="reset-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.signup.emailPlaceholder', 'you@example.com')}
        />
        <ErrorAlert message={error} />
        <PrimaryButton type="submit" loading={loading}>
          {t('auth.signin.sendResetCode', 'Send reset code')}
        </PrimaryButton>
        <TextLink
          onClick={() => {
            setStep('credentials');
            setError('');
          }}
        >
          {t('auth.signin.backToSignIn', 'Back to sign in')}
        </TextLink>
      </form>
    );
  }

  if (step === 'reset-code') {
    return (
      <form
        onSubmit={handleSubmitReset}
        className="flex flex-col gap-4"
        data-testid="signin-reset-code"
      >
        <FieldLabel htmlFor="reset-code">
          {t('auth.signin.codeLabel', 'Code from email')}
        </FieldLabel>
        <GlassInput
          id="reset-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123456"
        />
        <FieldLabel htmlFor="new-password">
          {t('auth.signin.newPasswordLabel', 'New password')}
        </FieldLabel>
        <GlassInput
          id="new-password"
          type="password"
          autoComplete="new-password"
          required
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="••••••••"
        />
        <ErrorAlert message={error} />
        <PrimaryButton type="submit" loading={loading}>
          {t('auth.signin.resetPassword', 'Reset password')}
        </PrimaryButton>
        <TextLink
          onClick={() => {
            setStep('credentials');
            setError('');
          }}
        >
          {t('auth.signin.backToSignIn', 'Back to sign in')}
        </TextLink>
      </form>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={handleGoogle}
        disabled={loading || googleInFlight}
        className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 text-small font-medium text-primary transition-colors hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 disabled:opacity-60"
      >
        {googleInFlight ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <GoogleIcon className="h-4 w-4" />
        )}
        {t('auth.signin.google', 'Continue with Google')}
      </button>

      <Divider label={t('auth.signin.or', 'Or continue with')} />

      <form onSubmit={handleSignIn} className="flex flex-col gap-4" data-testid="signin-form">
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="signin-email">
            {t('auth.signin.emailLabel', 'Email address')}
          </FieldLabel>
          <GlassInput
            id="signin-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.signup.emailPlaceholder', 'you@example.com')}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <FieldLabel htmlFor="signin-password">
              {t('auth.signin.passwordLabel', 'Password')}
            </FieldLabel>
            <TextLink
              onClick={() => {
                setStep('reset-request');
                setError('');
              }}
              className="text-caption"
            >
              {t('auth.signin.forgotPassword', 'Forgot password?')}
            </TextLink>
          </div>
          <div className="relative">
            <GlassInput
              id="signin-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="pr-11"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={
                showPassword
                  ? t('auth.signin.hidePassword', 'Hide password')
                  : t('auth.signin.showPassword', 'Show password')
              }
              className="absolute inset-y-0 right-3 flex items-center text-secondary hover:text-primary"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <ErrorAlert message={error} />

        <PrimaryButton type="submit" loading={loading}>
          {t('auth.signin.submit', 'Sign in')}
        </PrimaryButton>
      </form>
    </div>
  );
}

// ─── Shared primitives ──────────────────────────────────────────────────────

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="text-small font-medium text-secondary">
      {children}
    </label>
  );
}

function GlassInput({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        'w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-small text-primary placeholder:text-muted transition-colors',
        'focus:border-accent-lime/70 focus:bg-accent-lime/[0.06] focus:outline-none',
        className,
      )}
    />
  );
}

function PrimaryButton({
  loading,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) {
  return (
    <button
      {...rest}
      disabled={loading || rest.disabled}
      className={cn(
        'inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-accent-lime px-4 text-small font-semibold text-canvas transition-[filter,transform] duration-150',
        'hover:brightness-105 active:scale-[0.98]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400',
        'disabled:opacity-60',
        rest.className,
      )}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <>
          {children}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </>
      )}
    </button>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div className="relative flex items-center">
      <span className="h-px flex-1 bg-white/[0.08]" />
      <span className="px-3 font-mono text-caption uppercase tracking-[0.15em] text-muted">
        {label}
      </span>
      <span className="h-px flex-1 bg-white/[0.08]" />
    </div>
  );
}

function ErrorAlert({ message }: { message: string }) {
  if (!message) return null;
  return (
    <p
      role="alert"
      data-testid="auth-form-error"
      className="rounded-lg border border-accent-red/40 bg-accent-red/[0.08] px-3 py-2 text-caption text-primary"
    >
      {message}
    </p>
  );
}

function TextLink({
  onClick,
  className,
  children,
}: {
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-fit text-small text-accent-cyan transition-colors hover:underline',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400',
        className,
      )}
    >
      {children}
    </button>
  );
}
